/**
 * Shared types + the result contract every calculator obeys.
 *
 * Contract (non-negotiable, see design doc §0 / §1):
 *  - A calculator NEVER returns a plausible-but-wrong number for bad input.
 *  - Out-of-range / not-in-table inputs return { ok: false, error } with a
 *    clear, human-readable message.
 *  - Validation lives HERE, with the math, because the free web pages consume
 *    the same engine as the paid app. There is no second validation layer to
 *    rely on.
 */

/** Discriminated result. Callers must check `ok` before reading `value`. */
export type CalcResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

export function ok<T>(value: T): CalcResult<T> {
  return { ok: true, value };
}

export function err<T = never>(error: string): CalcResult<T> {
  return { ok: false, error };
}

/**
 * Conductor sizes WireCoder supports, smallest → largest. AWG sizes are
 * strings ("14".."4/0"); kcmil sizes are strings ("250".."500"). Kept as a
 * single union so callers can only ever pass a size that exists in our tables.
 */
export const AWG_SIZES = [
  "14",
  "12",
  "10",
  "8",
  "6",
  "4",
  "3",
  "2",
  "1",
  "1/0",
  "2/0",
  "3/0",
  "4/0",
  "250",
  "300",
  "350",
  "400",
  "500",
] as const;

export type AwgSize = (typeof AWG_SIZES)[number];

export function isAwgSize(value: unknown): value is AwgSize {
  return typeof value === "string" && (AWG_SIZES as readonly string[]).includes(value);
}

export type ConductorMaterial = "copper" | "aluminum";

/** Insulation temperature rating column used in Table 310.16. */
export type TempRating = "60C" | "75C" | "90C";

/**
 * Guard helper: a finite, positive number. Used everywhere to reject NaN,
 * Infinity, negatives, and zero where they make no physical sense.
 */
export function isPositiveFinite(n: unknown): n is number {
  return typeof n === "number" && Number.isFinite(n) && n > 0;
}

export function isFiniteNumber(n: unknown): n is number {
  return typeof n === "number" && Number.isFinite(n);
}

export function isNonNegativeInteger(n: unknown): n is number {
  return typeof n === "number" && Number.isInteger(n) && n >= 0;
}
